import React, { useState, useMemo } from 'react';
import { UserPlus, Search, ShieldCheck } from 'lucide-react';
import AddAdvisorModal from './AddAdvisorModal';

// Status pill copy + tone. Maps the AdvisoryTeamMember.status enum
// (added / invited / active / off_platform / exploring_options) to the
// pill's label and Tailwind color combo. not_added doesn't render a
// pill (the seat's empty state speaks for itself).
const STATUS_PRESENTATION = {
  added:              { label: 'Added',     classes: 'bg-muted text-muted-foreground' },
  invited:            { label: 'Invited',   classes: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  active:             { label: 'Connected', classes: 'bg-[hsl(var(--succeedo-orange))]/10 text-[hsl(var(--succeedo-orange))]' },
  off_platform:       { label: 'Off-platform', classes: 'bg-muted text-muted-foreground' },
  exploring_options:  { label: 'Exploring', classes: 'bg-muted text-muted-foreground' },
};

function initialsFor(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

export default function SuccessionTeamBlock({
  members = [],
  hasAssessment = false,
  onAddMine,                // ({ role, name, email, phone, firm, send_invite }) => Promise<void>
  onFindOne,                // (role) => void  -- usually navigates
}) {
  const [modalRole, setModalRole]   = useState(null);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const filledMembers = useMemo(
    () => members.filter((m) => m && m.status && m.status !== 'not_added'),
    [members],
  );

  const total  = members.length || 6;
  const filled = filledMembers.length;

  const headline = useMemo(() => {
    if (filled >= total) return 'Your succession team';
    if (!hasAssessment) return 'Start with your team';
    return 'Your succession team';
  }, [filled, total, hasAssessment]);

  const blurb = useMemo(() => {
    if (filled >= total) return 'All seats filled — well done. Update or replace anyone via their card.';
    if (!hasAssessment) return 'Add the advisors you already have, or find new ones — no questionnaire required.';
    return 'Connect with the people who help you exit successfully.';
  }, [filled, total, hasAssessment]);

  const handleAddSeat = (role) => {
    setSubmitError('');
    setModalRole(role);
    if (typeof window !== 'undefined' && window.ahoy?.track) {
      try { window.ahoy.track('succeedo_dashboard_team_add_existing_clicked', { role }); } catch (_e) {}
    }
  };

  const handleFindSeat = (role) => {
    if (typeof window !== 'undefined' && window.ahoy?.track) {
      try { window.ahoy.track('succeedo_dashboard_team_find_clicked', { role }); } catch (_e) {}
    }
    onFindOne?.(role);
  };

  const handleSubmit = async (payload) => {
    setSubmitError('');
    setSubmitBusy(true);
    try {
      await onAddMine?.(payload);
      setModalRole(null);
      if (typeof window !== 'undefined' && window.ahoy?.track) {
        try { window.ahoy.track('succeedo_dashboard_team_add_existing_saved', { role: payload.role }); } catch (_e) {}
      }
    } catch (e) {
      setSubmitError(e?.message || 'Could not save. Please try again.');
    } finally {
      setSubmitBusy(false);
    }
  };

  return (
    <section
      id="succession-team-block"
      className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6 mb-8"
    >
      <header className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-[24px] font-light text-foreground tracking-[-0.01em] mb-1">
            {headline}
          </h2>
          <p className="text-[13px] text-muted-foreground max-w-xl">{blurb}</p>
        </div>
        <div className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground self-end">
          <span className="text-[hsl(var(--succeedo-orange))] font-medium">{filled}</span> of {total} seats filled
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
        {members.map((member) => (
          <Seat
            key={member.role}
            member={member}
            onAddMine={() => handleAddSeat(member.role)}
            onFindOne={() => handleFindSeat(member.role)}
          />
        ))}
      </div>

      {modalRole && (
        <AddAdvisorModal
          role={modalRole}
          roleLabel={members.find((m) => m.role === modalRole)?.role_label || modalRole}
          busy={submitBusy}
          error={submitError}
          onClose={() => { if (!submitBusy) { setModalRole(null); setSubmitError(''); } }}
          onSubmit={handleSubmit}
        />
      )}
    </section>
  );
}

function Seat({ member, onAddMine, onFindOne }) {
  const isFilled = member.status && member.status !== 'not_added';
  const presentation = STATUS_PRESENTATION[member.status] || null;

  if (isFilled) {
    return (
      <div className="flex flex-col items-center text-center min-w-0">
        <div className="w-12 h-12 rounded-full bg-[hsl(var(--succeedo-orange))]/15 text-[hsl(var(--succeedo-orange))] flex items-center justify-center font-medium text-[14px] mb-2">
          {initialsFor(member.name)}
        </div>
        <div className="text-[13px] font-medium text-foreground truncate w-full" title={member.name || ''}>
          {member.name || '—'}
        </div>
        <div className="text-[11px] text-muted-foreground truncate w-full mb-2" title={member.role_label}>
          {member.role_label}
        </div>
        {presentation && (
          <span className={`font-mono text-[9px] uppercase tracking-wide rounded-full px-2 py-0.5 ${presentation.classes}`}>
            {presentation.label}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center min-w-0">
      <div className="w-12 h-12 rounded-full border border-dashed border-muted-foreground/40 flex items-center justify-center text-muted-foreground/50 mb-2">
        <ShieldCheck className="w-5 h-5" />
      </div>
      <div className="text-[12px] text-muted-foreground mb-2 truncate w-full" title={member.role_label}>
        {member.role_label}
      </div>
      <button
        type="button"
        onClick={onAddMine}
        className="inline-flex items-center gap-1 text-[12px] text-[hsl(var(--succeedo-orange))] font-medium hover:underline"
      >
        <UserPlus className="w-3 h-3" /> Add mine
      </button>
      <button
        type="button"
        onClick={onFindOne}
        className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground transition-colors mt-1"
      >
        <Search className="w-3 h-3" /> Find one →
      </button>
    </div>
  );
}
